import Text from "../../../Text";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog.tsx";
import { useEffect, type FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form.tsx";
import { Input } from "../../../ui/input.tsx";
import { Button } from "../../../ui/button.tsx";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Link, useLocation, useNavigate } from "react-router";
import { signUpUser, userState } from "@/store/userState.ts";
import { signInWithRedirect } from "aws-amplify/auth";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";

const formSchema = z.object({
  fullname: z.string().min(2, {
    error: "Le nom complet doit contenir au moins 2 lettres",
  }),
  email: z.email({ error: "Email invalide" }),
  password: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      {
        error: "Mot de passe invalide",
      },
    ),
});

const getMessageError = (
  userError: Error,
  t: TFunction<"translation", undefined>,
): string => {
  if (["NetworkError"].includes(userError.message.split(":")[0]))
    return t("network_error");

  return userError.message;
};

const regexUppercase = /^(?=.*[A-Z])[A-Za-z\d@$!%*?&]{1,}$/;
const regex8Char = /^[A-Za-z\d@$!%*?&]{8,}$/;
const regexNumber = /^(?=.*\d)[A-Za-z\d@$!%*?&]{1,}$/;
const regexSymbol = /^(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{1,}$/;

const SignUp: FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userData, error: userError, signUpSucceed } = userState();

  const transformedPathname = pathname.replace("/sign-up", "");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullname: "",
      email: "",
      password: "",
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    signUpUser(values);
  };

  useEffect(() => {
    if (userData) {
      navigate("/");
    }
  }, [userData]);

  useEffect(() => {
    if (signUpSucceed) {
      toast.success(t("signup_account_created"), {
        duration: 2000,
      });
      navigate(`${transformedPathname}/confirm`);
    }
  }, [signUpSucceed, transformedPathname]);

  return (
    <DialogContent
      showCloseButton={false}
      className="w-[370px] overflow-y-auto"
    >
      <VisuallyHidden>
        <DialogTitle>Sign Up</DialogTitle>
      </VisuallyHidden>
      <DialogHeader>
        <VisuallyHidden>
          <DialogDescription>Sign Up here</DialogDescription>
        </VisuallyHidden>
        <img
          src="/svg/krunsh-logo.svg"
          alt="krunsh logo"
          height={36}
          width={120}
        />
        <Text fontWeight={600} fontSize={20} color="#44546F">
          {t("auth_title")}
        </Text>
        <Text fontWeight={400} fontSize={14} color="#44546F">
          {t("auth_subtitle")}
        </Text>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="gap-[12px] flex flex-col">
            <FormField
              control={form.control}
              name="fullname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("fullname")}</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("email")}</FormLabel>
                  <FormControl>
                    <Input placeholder="johndoe@gmail.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("password")}</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="*********" {...field} />
                  </FormControl>
                  <FormDescription>
                    <span
                      style={{
                        color: regex8Char.test(field.value) ? "#00A408" : "",
                      }}
                    >
                      {t("password_requirements_size")}
                    </span>
                    <span
                      style={{
                        color: regexUppercase.test(field.value)
                          ? "#00A408"
                          : "",
                      }}
                    >{` ${t("password_requirements_uppercase")},`}</span>
                    <span
                      style={{
                        color: regexNumber.test(field.value) ? "#00A408" : "",
                      }}
                    >{` ${t("password_requirements_number")}`}</span>
                    <span
                      style={{
                        color: regexSymbol.test(field.value) ? "#00A408" : "",
                      }}
                    >
                      {` ${t("password_requirements_symbol")}`}
                    </span>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button
            type="submit"
            variant="primaryFilled"
            className="my-[24px] w-[100%]"
          >
            {t("sign_up")}
          </Button>
          <Text fontSize={14} fontWeight={400} color="#414651">
            {t("or_sign_up_with_email")}
          </Text>
          <button
            type="button"
            onClick={() => {
              signInWithRedirect({ provider: "Google" });
            }}
            className="px-[16px] w-[100%] py-[8px] flex items-center justify-center bg-[white] rounded-[4px] gap-[8px] border border-[#F1F2F4] mt-[8px]"
          >
            <img
              height={24}
              width={24}
              src="/svg/google-icon.svg"
              alt="google icon"
            />
            <Text fontSize={14} fontWeight={600}>
              Google
            </Text>
          </button>
          <div className="flex items-center justify-center gap-[4px] mt-[24px] w-[100%]">
            <Text>{t("already_sign_up")}</Text>

            <Link to={`${transformedPathname}/sign-in`}>
              <Text color="#0F62FE">{t("sign_in")}</Text>
            </Link>
          </div>
        </form>
      </Form>
      {userError &&
        userError.message.split(":")[0] !== "UserUnAuthenticatedException" && (
          <Alert variant="destructive">
            <AlertTitle>{t("auth_error_title")}</AlertTitle>
            <AlertDescription>{getMessageError(userError, t)}</AlertDescription>
          </Alert>
        )}
    </DialogContent>
  );
};

export default SignUp;
